import React, {useEffect, useRef, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";

import TipNavbar from "../../components/TipNavbar";
import {
    getBusinessData,
    getEmployeeByTipID, getPublicBusinessData,
    getPublicEmployeeByTipID,
    submitFeedbackOnly
} from "../../services/firestoreService";

// projectId is either tiply-app-test or tiply-app. This is to determine which database connection to use.
const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;



function TipPageV3() {
    const navigate = useNavigate();

    const {businessID, tipID} = useParams();
    const [loading, setLoading] = useState(false);
    const [businessName, setBusinessName] = useState(null);
    const [googlePlaceID, setGooglePlaceID] = useState("");
    const [employee, setEmployee] = useState(null); // TODO use more universal name
    const [error, setError] = useState(null);
    const [selectedAmount, setSelectedAmount] = useState(0);
    const [customMode, setCustomMode] = useState(false);
    const [rating, setRating] = useState(5);
    const [feedback, setFeedback] = useState("");
    const [tipOptions, setTipOptions] = useState([2, 4, 8, 10]); // fallback default



    const inputRef = useRef(null);

    const handleSelectInput = () => {
        inputRef.current?.select();
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const businessData = await getPublicBusinessData(businessID);
                if (!businessData) { setError("Business not found"); return; }

                // business basics
                setBusinessName(businessData.displayName);
                setGooglePlaceID(businessData?.googlePlaceID || "");
                setTipOptions(businessData.tipAmountConfig || [2, 4, 8, 10]);
                setSelectedAmount(
                    Number.isInteger(businessData.defaultTipAmount)
                        ? businessData.defaultTipAmount
                        : (businessData.tipAmountConfig?.[0] || 4)
                );

                // NEW: config flags
                const tipsConfig = businessData?.config?.tips || {};
                const teamEnabled = businessData.teamEnabled;
                const personalEnabled = businessData.personalEnabled;

                const numericTipId = parseInt(tipID, 10);
                const isTeamUrl = numericTipId === 1;

                // TEAM URL (/.../1)
                if (isTeamUrl) {
                    console.log("team enabled: " + teamEnabled);
                    if (!teamEnabled) { setError("Team tips are disabled."); return; }
                    // synthesize a "team" pseudo-employee for UI
                    setEmployee({
                        id: "team",
                        displayName: tipsConfig?.team?.label || "Team",
                        imgUrl: businessData?.teamImgUrl || "", // optional business logo fallback
                        accountStatus: "active",
                        role: "team",
                    });
                    return;
                }

                // PERSONAL URL
                if (!personalEnabled) { setError("Personal tips are disabled."); return; }

                const employeeData = await getPublicEmployeeByTipID(businessID, numericTipId);
                if (!employeeData) { setError("Employee not found"); return; }

                // status checks (same as before)
                if (employeeData.accountStatus === "deactivated") { setError("This member is no longer accepting tips."); return; }
                if (employeeData.accountStatus === "unverified") { setError("Member not found."); return; }
                if (employeeData.accountStatus === "readonly") { setError("Member not found"); return; }

                setEmployee(employeeData);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Something went wrong. Please try again.");
            }
        };

        fetchData();
    }, [businessID, tipID]);


    const handlePaymentMontonio = async () => {
        setLoading(true);
        try {
            if (selectedAmount > 0) {
                const response = await fetch(
                    `https://us-central1-${projectId}.cloudfunctions.net/createMontonioPayment`,
                    {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({
                            amount: selectedAmount,
                            businessID,
                            employeeID: employee.id,
                            rating,
                            feedback,
                            businessName,
                            employeeName: employee.displayName,
                            googlePlaceID: googlePlaceID || "",
                            employeeImgUrl: employee.imgUrl,
                        }),
                    }
                );

                const data = await response.json();
                if (data.payment_url) {
                    window.location.href = data.payment_url;
                } else {
                    alert("Failed to generate payment link.");
                }
            } else {

                // No tip, just feedback — save directly to Firestore
                const docID = await submitFeedbackOnly(
                    businessID,
                    employee.id,
                    rating,
                    feedback,
                    businessName,
                    employee.displayName,
                    googlePlaceID,
                    employee.imgUrl
                );

                if (docID) {
                    navigate(`/tip/success?tipID=${docID}`);
                } else {
                    alert("Something went wrong submitting your feedback.");
                }

            }
        } catch (error) {
            console.error("Error during submission:", error);
            alert("Something went wrong.");
        }
        setLoading(false);
    };

    const handlePaymentStripe = async () => {
        setLoading(true);
        try {
            if (selectedAmount > 0) {
                const response = await fetch(
                    `https://us-central1-${projectId}.cloudfunctions.net/createStripePayment`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            amount: selectedAmount,
                            businessID,
                            employeeID: employee.id, // TODO is this necessary?
                            rating,
                            feedback,
                            businessName,
                            employeeName: employee.displayName, // TODO is this wording correct? We should have a more universal name if its a Team, not an individual Employee
                            googlePlaceID: googlePlaceID || "",
                            employeeImgUrl: employee.imgUrl, // TODO again same the wording should be universal if its a Team, not an individual Employee
                            tipID: tipID,
                        }),
                    }
                );

                const data = await response.json();
                if (data.payment_url) {
                    window.location.href = data.payment_url; // ✅ Stripe Checkout redirect
                } else {
                    alert("Failed to generate payment link.");
                }
            } else {
                // No tip, just feedback — save directly to Firestore
                const docID = await submitFeedbackOnly(
                    businessID,
                    employee.id, // TODO Wording more universal, also is this necessary?
                    rating,
                    feedback,
                    businessName,
                    employee.displayName, // TODO wording more universal
                    googlePlaceID,
                    employee.imgUrl // TODO wording more universal
                );

                if (docID) {
                    navigate(`/tip/success?tipID=${docID}`);
                } else {
                    alert("Something went wrong submitting your feedback.");
                }
            }
        } catch (error) {
            console.error("Error during submission:", error);
            alert("Something went wrong.");
        }
        setLoading(false);
    };



    if (error) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500 text-lg">
                {error}
            </div>
        );
    }

    if (!businessName || !employee) {
        console.log("businessName: " + businessName)
        console.log("employee: " + employee)
        return (
            <div className="flex flex-col items-center justify-center h-screen text-gray-500 text-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-300 border-t-blue-500"></div>
            </div>
        );
    }

    const isTeam = employee.displayName?.toLowerCase() === "team";
    const thanksMessage = isTeam
        ? `Thanks for tipping our Team at ${businessName}!`
        : `Thanks for tipping ${employee.displayName} at ${businessName}!`;


    return (
        <div className="relative min-h-screen bg-gray-100 overflow-hidden">
            {/* Top Wave */}
            <div className="absolute top-[48px] left-0 w-full z-[0] pointer-events-none">
                <svg
                    viewBox="0 0 1440 320"
                    className="w-full h-64 scale-x-[-1]"
                    preserveAspectRatio="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill="#00ffbe"
                        d="M0,160 C360,240 1080,80 1440,160 L1440,0 L0,0 Z"
                    />
                </svg>
            </div>

            {/* Gradient Diagonal Lines */}
            <div className="absolute inset-0 z-[0] pointer-events-none">
                <svg
                    viewBox="0 0 300 110"
                    className="w-full h-full"
                    preserveAspectRatio="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#7ecbff"/>
                            <stop offset="100%" stopColor="#7fffd4"/>
                        </linearGradient>
                    </defs>

                    <line x1="100" y1="120" x2="500" y2="50" stroke="url(#lineGradient)" strokeWidth="4"/>
                    <line x1="140" y1="120" x2="540" y2="50" stroke="url(#lineGradient)" strokeWidth="4"/>
                    <line x1="180" y1="120" x2="580" y2="50" stroke="url(#lineGradient)" strokeWidth="4"/>
                    <line x1="220" y1="120" x2="620" y2="50" stroke="url(#lineGradient)" strokeWidth="4"/>
                </svg>
            </div>

            {/* Navbar */}
            <div className="relative z-20">
                <TipNavbar/>
            </div>

            {/* Main content */}
            <div className="relative z-10 flex flex-col items-center pt-8 px-2">
                <div className="relative w-full">
                    <div className="absolute left-0 right-0 -bottom-4 z-10">
                        <div
                            className="bg-blue-500 text-white max-w-md mx-auto font-extrabold text-lg py-2 rounded-xl shadow text-center w-full">
                            {employee.displayName.toUpperCase()}
                        </div>
                    </div>

                    <div
                        className="mx-auto w-48 h-48 rounded-full p-[8px] bg-gradient-to-br from-cyan-400 to-emerald-400 z-0 relative">
                        <img
                            src={employee.imgUrl}
                            alt={`${employee.displayName}`}
                            className="w-full h-full rounded-full object-cover"
                        />
                    </div>
                </div>

                <h1 className="text-2xl font-extrabold mb-4 max-w-md uppercase text-center mt-8">
                    {thanksMessage}
                </h1>

                {/* ─── CARD (V3 refined internals) ─── */}
                <div className="bg-white w-full max-w-md p-5 rounded-2xl shadow-lg shadow-black/10 text-center"
                     style={{boxShadow: '0 0 12px rgba(0, 0, 0, 0.1)'}}>

                    {/* Tip amount buttons */}
                    <div className="flex justify-center gap-3 mb-4">
                        {tipOptions.map((amount) => (
                            <button
                                key={amount}
                                onClick={() => {
                                    setSelectedAmount(amount);
                                    setCustomMode(false);
                                }}
                                className={`flex-1 py-2.5 rounded-xl text-lg font-extrabold transition-all duration-150 ${
                                    selectedAmount === amount && !customMode
                                        ? "bg-greenmint text-black shadow-md shadow-greenmint/30 scale-[1.03]"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                &euro;{amount}
                            </button>
                        ))}
                    </div>

                    {/* Custom amount input */}
                    <div className="mb-5">
                        <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-greenmint/50 focus-within:bg-white transition">
                            <span className="text-xl font-extrabold text-gray-400">&euro;</span>
                            <input
                                ref={inputRef}
                                type="number"
                                value={selectedAmount}
                                onChange={(e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || value === 0) {
                                        setSelectedAmount(0);
                                    } else if (value < 1) {
                                        setSelectedAmount(1);
                                    } else {
                                        setSelectedAmount(Math.floor(value * 100) / 100);
                                    }
                                }}
                                onFocus={handleSelectInput}
                                className="flex-1 bg-transparent text-xl font-extrabold text-gray-900 text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                            </svg>
                        </div>
                    </div>

                    {/* Rating */}
                    <div className="mb-4">
                        <p className="text-lg font-bold text-gray-900 mb-2">Rate your experience</p>
                        <div className="flex justify-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className={`transition-transform hover:scale-110 ${
                                        rating >= star ? "text-yellow-400 drop-shadow-sm" : "text-gray-300"
                                    }`}
                                    aria-label={`Rate ${star} star`}
                                >
                                    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Comment */}
                    <div className="text-left mb-5">
                        <label htmlFor="feedback" className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Leave a comment (optional)
                        </label>
                        <textarea
                            id="feedback"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            rows="2"
                            maxLength={500}
                            className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-greenmint/50 focus:border-greenmint resize-none text-gray-700 placeholder-gray-400 transition-all"
                            placeholder="Your feedback..."
                        />
                    </div>

                    {/* Submit button */}
                    <button
                        onClick={handlePaymentStripe}
                        disabled={loading}
                        className={`w-full py-3 rounded-xl bg-black hover:bg-neutral-800 text-white font-extrabold text-lg transition-all ${
                            loading ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="h-5 w-5 border-[3px] border-white border-t-transparent rounded-full animate-spin"/>
                                Processing...
                            </span>
                        ) : selectedAmount > 0 ? (
                            `TIP €${selectedAmount}`
                        ) : (
                            "Submit"
                        )}
                    </button>
                </div>
            </div>

            <div className="relative z-10 text-xs text-center text-gray-600 px-4 py-6 leading-snug">
                <p className="mt-1">
                    By tipping you agree to our{" "}
                    <a href="/terms" target="_blank" className="underline font-medium">Terms of use</a>,{" "}
                    <a href="/privacy" target="_blank" className="underline font-medium">Privacy Policy</a>.
                </p>
            </div>
        </div>
    );
}

export default TipPageV3;
